import { useEffect } from 'react';
import { splatForStep, type SignupStep } from '@/pages/signup/utils/steps';

type UseCanonicalSignupUrlArgs = {
  /** The route's raw `$` param. */
  splat: string | undefined;
  /** The step that splat resolved to, or `null` for an unknown segment. */
  step: SignupStep | null;
  /**
   * Only rewrite while Hydra serves the funnel. With the rollout flag off the
   * embedded app owns these URLs, including segments Hydra has no step for, so
   * rewriting would pull the URL out from under it.
   */
  isMigrationEnabled: boolean;
  /** Navigate to the canonical splat. Must replace, not push. */
  onCanonicalize: (splat: string) => void;
};

/**
 * Keeps the URL in step with what the funnel actually renders, so a deeper
 * splat like `/signup/password/extra` doesn't leave the address bar holding a
 * URL the funnel never resolved. Unknown segments resolve to no step at all
 * and are redirected by the route instead, so they are left alone here.
 */
export function useCanonicalSignupUrl({
  splat,
  step,
  isMigrationEnabled,
  onCanonicalize,
}: UseCanonicalSignupUrlArgs): void {
  const currentSplat = splat ?? '';
  const canonicalSplat = step == null ? null : splatForStep(step);

  useEffect(() => {
    if (!isMigrationEnabled || canonicalSplat == null || currentSplat === canonicalSplat) {
      return;
    }
    onCanonicalize(canonicalSplat);
  }, [isMigrationEnabled, currentSplat, canonicalSplat, onCanonicalize]);
}
